import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/context/AuthContext';
import { useBranch } from '@/context/BranchContext';
import { formatMoney, formatDateTime } from '@/lib/format';
import { PAYMENT_LABELS } from '@/lib/types';
import type { Sale } from '@/lib/types';

interface Stats {
  ventasHoy: number;
  facturasHoy: number;
  productos: number;
  stockBajo: number;
  agotados: number;
  cajeros: number;
}

interface BranchSales {
  name: string;
  total: number;
}

export default function Home() {
  const { profile, isAdmin } = useAuth();
  const { branches, activeBranchId, activeBranch } = useBranch();
  const [stats, setStats] = useState<Stats>({
    ventasHoy: 0,
    facturasHoy: 0,
    productos: 0,
    stockBajo: 0,
    agotados: 0,
    cajeros: 0,
  });
  const [recentSales, setRecentSales] = useState<Sale[]>([]);
  const [branchSales, setBranchSales] = useState<BranchSales[]>([]);
  const [lowStock, setLowStock] = useState<{ id: string; name: string; stock: number; min: number }[]>([]);
  const [outOfStock, setOutOfStock] = useState<{ id: string; name: string; stock: number; min: number }[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fullName = [profile?.first_name, profile?.last_name].filter(Boolean).join(' ') || 'Usuario';

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);

        let todayQuery = supabase
          .from('sales')
          .select('total')
          .gte('created_at', startOfToday.toISOString());
        let recentQuery = supabase.from('sales').select('*');
        if (activeBranchId) {
          todayQuery = todayQuery.eq('branch_id', activeBranchId);
          recentQuery = recentQuery.eq('branch_id', activeBranchId);
        }
        recentQuery = recentQuery.order('created_at', { ascending: false }).limit(8);

        const productsQuery = supabase.from('products').select('id, name, min_stock, active');
        let invQuery = supabase.from('branch_inventory').select('product_id, stock');
        if (activeBranchId) invQuery = invQuery.eq('branch_id', activeBranchId);

        const [todayRes, recentRes, productsRes, invRes, cashiersRes] = await Promise.all([
          todayQuery,
          recentQuery,
          productsQuery,
          invQuery,
          supabase.from('profiles').select('id').eq('role', 'cashier'),
        ]);

        if (todayRes.error) throw todayRes.error;
        if (recentRes.error) throw recentRes.error;
        if (productsRes.error) throw productsRes.error;
        if (invRes.error) throw invRes.error;

        if (!active) return;

        const todayRows = todayRes.data ?? [];
        const ventasHoy = todayRows.reduce((s, r) => s + Number(r.total || 0), 0);

        const productRows = (productsRes.data ?? []) as {
          id: string;
          name: string;
          min_stock: number | null;
          active: boolean;
        }[];
        const productos = productRows.length;

        const stockMap = new Map<string, number>();
        for (const r of (invRes.data ?? []) as { product_id: string; stock: number }[]) {
          stockMap.set(r.product_id, (stockMap.get(r.product_id) ?? 0) + Number(r.stock ?? 0));
        }

        let stockBajo = 0;
        let agotados = 0;
        const low: { id: string; name: string; stock: number; min: number }[] = [];
        const out: { id: string; name: string; stock: number; min: number }[] = [];
        for (const p of productRows) {
          if (!p.active) continue;
          const stock = stockMap.get(p.id) ?? 0;
          const min = Number(p.min_stock ?? 0);
          if (stock <= 0) {
            agotados++;
            out.push({ id: p.id, name: p.name, stock, min });
          } else if (stock <= min) {
            stockBajo++;
            low.push({ id: p.id, name: p.name, stock, min });
          }
        }
        low.sort((a, b) => a.stock - b.stock);
        out.sort((a, b) => a.name.localeCompare(b.name));
        setLowStock(low.slice(0, 6));
        setOutOfStock(out.slice(0, 6));

        setStats({
          ventasHoy,
          facturasHoy: todayRows.length,
          productos,
          stockBajo,
          agotados,
          cajeros: (cashiersRes.data ?? []).length,
        });
        setRecentSales((recentRes.data as Sale[]) ?? []);

        if (isAdmin) {
          const { data: allSales } = await supabase.from('sales').select('branch_id, total');
          const map = new Map<string, number>();
          for (const s of (allSales ?? []) as { branch_id: string | null; total: number }[]) {
            if (!s.branch_id) continue;
            map.set(s.branch_id, (map.get(s.branch_id) ?? 0) + Number(s.total ?? 0));
          }
          const breakdown = branches.map((b) => ({ name: b.name, total: map.get(b.id) ?? 0 }));
          if (active) setBranchSales(breakdown);
        }
      } catch (err) {
        if (active) setError(err instanceof Error ? err.message : 'No se pudieron cargar los datos');
      } finally {
        if (active) setLoading(false);
      }
    };
    fetchData();
    return () => {
      active = false;
    };
  }, [activeBranchId, branches, isAdmin]);

  const heading = activeBranch
    ? `Sucursal: ${activeBranch.name}`
    : isAdmin
      ? 'Todas las sucursales'
      : 'Resumen';

  const statCards = [
    { label: 'Ventas de hoy', value: formatMoney(stats.ventasHoy), icon: 'ri-money-dollar-circle-line', color: 'bg-primary-100 text-primary-700' },
    { label: 'Facturas de hoy', value: String(stats.facturasHoy), icon: 'ri-receipt-line', color: 'bg-accent-100 text-accent-700' },
    { label: 'Productos', value: String(stats.productos), icon: 'ri-box-3-line', color: 'bg-secondary-100 text-secondary-900' },
    { label: 'Stock bajo', value: String(stats.stockBajo), icon: 'ri-alert-line', color: 'bg-accent-100 text-accent-700' },
    { label: 'Agotados', value: String(stats.agotados), icon: 'ri-forbid-line', color: 'bg-secondary-100 text-secondary-900' },
  ];

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            Bienvenido, {fullName}
          </h1>
          <p className="mt-1 text-foreground-600 flex items-center gap-2">
            <i className="ri-building-2-line" /> {heading}
          </p>
        </div>
        <Link
          to="/pos"
          className="inline-flex items-center justify-center gap-2 rounded-md bg-primary-500 hover:bg-primary-600 px-4 py-2.5 text-sm font-semibold text-background-50 whitespace-nowrap transition cursor-pointer"
        >
          <i className="ri-shopping-cart-2-line" /> Nueva venta
        </Link>
      </div>

      {error && (
        <div className="mt-6 rounded-lg border border-accent-200 bg-accent-50 px-4 py-3 flex items-center gap-3">
          <i className="ri-error-warning-line text-accent-700" />
          <p className="text-sm text-foreground-700">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="ml-auto text-sm font-medium text-accent-700 hover:underline cursor-pointer"
          >
            Reintentar
          </button>
        </div>
      )}

      {loading ? (
        <div className="mt-8 flex items-center justify-center py-20">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin" />
        </div>
      ) : (
        <>
          <div className="mt-6 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {statCards.map((card) => (
              <div key={card.label} className="rounded-lg border border-background-200 bg-background-50 p-4">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${card.color}`}>
                  <i className={`${card.icon} text-lg`} />
                </div>
                <p className="mt-3 text-lg sm:text-2xl font-heading font-bold text-foreground-950 leading-tight break-words">
                  {card.value}
                </p>
                <p className="mt-0.5 text-xs text-foreground-600">{card.label}</p>
              </div>
            ))}
          </div>

          {(lowStock.length > 0 || outOfStock.length > 0) && (
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                    <i className="ri-alert-line text-secondary-600" /> Stock bajo
                  </h2>
                  {isAdmin && (
                    <Link to="/inventory" className="text-sm font-medium text-primary-700 hover:underline cursor-pointer">
                      Ver inventario
                    </Link>
                  )}
                </div>
                {lowStock.length === 0 ? (
                  <p className="mt-4 text-sm text-foreground-600">Sin productos en nivel bajo.</p>
                ) : (
                  <ul className="mt-4 divide-y divide-background-100">
                    {lowStock.map((p) => (
                      <li key={p.id} className="flex items-center justify-between py-2.5">
                        <span className="text-sm text-foreground-900 truncate">{p.name}</span>
                        <span className="text-sm font-semibold text-secondary-700 shrink-0 ml-3">
                          {p.stock} unidades
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg font-semibold text-foreground-950 flex items-center gap-2">
                    <i className="ri-forbid-line text-primary-600" /> Productos agotados
                  </h2>
                  {isAdmin && (
                    <Link to="/inventory" className="text-sm font-medium text-primary-700 hover:underline cursor-pointer">
                      Registrar entrada
                    </Link>
                  )}
                </div>
                {outOfStock.length === 0 ? (
                  <p className="mt-4 text-sm text-foreground-600">No hay productos agotados.</p>
                ) : (
                  <ul className="mt-4 divide-y divide-background-100">
                    {outOfStock.map((p) => (
                      <li key={p.id} className="flex items-center justify-between py-2.5">
                        <span className="text-sm text-foreground-900 truncate">{p.name}</span>
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary-100 text-primary-800 shrink-0 ml-3">
                          Agotado
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {isAdmin && (
            <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h2 className="font-heading text-lg font-semibold text-foreground-950">
                  Ventas por sucursal
                </h2>
                <div className="mt-3 flex items-center gap-4 text-sm text-foreground-600">
                  <span className="flex items-center gap-1.5">
                    <i className="ri-building-2-line" /> {branches.length} sucursales
                  </span>
                  <span className="flex items-center gap-1.5">
                    <i className="ri-team-line" /> {stats.cajeros} cajeros
                  </span>
                </div>
                {branchSales.length === 0 ? (
                  <p className="mt-4 text-sm text-foreground-600">Aún no hay sucursales.</p>
                ) : (
                  <ul className="mt-4 divide-y divide-background-100">
                    {branchSales.map((b) => (
                      <li key={b.name} className="flex items-center justify-between py-2.5">
                        <span className="text-sm text-foreground-900">{b.name}</span>
                        <span className="text-sm font-semibold text-foreground-950">
                          {formatMoney(b.total)}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <div className="flex items-center justify-between">
                  <h2 className="font-heading text-lg font-semibold text-foreground-950">
                    Últimas ventas
                  </h2>
                  <Link to="/sales" className="text-sm font-medium text-primary-700 hover:underline cursor-pointer">
                    Ver todas
                  </Link>
                </div>
                {recentSales.length === 0 ? (
                  <p className="mt-4 text-sm text-foreground-600">
                    Aún no hay ventas registradas. ¡Crea tu primera venta desde el POS!
                  </p>
                ) : (
                  <ul className="mt-4 divide-y divide-background-100">
                    {recentSales.slice(0, 5).map((sale) => (
                      <li key={sale.id} className="flex items-center justify-between py-2.5">
                        <div className="min-w-0">
                          <Link
                            to={`/sales/${sale.id}`}
                            className="text-sm font-medium text-primary-700 hover:underline cursor-pointer"
                          >
                            {sale.invoice_number}
                          </Link>
                          <p className="text-xs text-foreground-500">
                            {sale.customer_name || 'Cliente general'} · {formatDateTime(sale.created_at)}
                          </p>
                        </div>
                        <div className="text-right">
                          <span className="text-sm font-semibold text-foreground-950">
                            {formatMoney(Number(sale.total))}
                          </span>
                          <span className="block text-xs text-foreground-500">
                            {PAYMENT_LABELS[sale.payment_method] || sale.payment_method}
                          </span>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {!isAdmin && (
            <div className="mt-8">
              <div className="flex items-center justify-between">
                <h2 className="font-heading text-lg font-semibold text-foreground-950">
                  Últimas ventas
                </h2>
                <Link to="/sales" className="text-sm font-medium text-primary-700 hover:underline cursor-pointer">
                  Ver todas
                </Link>
              </div>
              {recentSales.length === 0 ? (
                <div className="mt-4 rounded-lg border border-dashed border-background-300 p-10 text-center">
                  <i className="ri-receipt-line text-3xl text-foreground-400" />
                  <p className="mt-3 text-sm text-foreground-600">
                    Aún no hay ventas registradas. ¡Crea tu primera venta desde el POS!
                  </p>
                </div>
              ) : (
                <div className="mt-4 rounded-lg border border-background-200 overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-background-100 text-foreground-600 text-left">
                      <tr>
                        <th className="px-4 py-3 font-medium">Factura</th>
                        <th className="px-4 py-3 font-medium hidden sm:table-cell">Cliente</th>
                        <th className="px-4 py-3 font-medium hidden md:table-cell">Método</th>
                        <th className="px-4 py-3 font-medium hidden md:table-cell">Fecha</th>
                        <th className="px-4 py-3 font-medium text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-background-200">
                      {recentSales.map((sale) => (
                        <tr key={sale.id} className="hover:bg-background-100 transition">
                          <td className="px-4 py-3">
                            <Link
                              to={`/sales/${sale.id}`}
                              className="font-medium text-primary-700 hover:underline cursor-pointer"
                            >
                              {sale.invoice_number}
                            </Link>
                          </td>
                          <td className="px-4 py-3 text-foreground-700 hidden sm:table-cell">
                            {sale.customer_name || 'Cliente general'}
                          </td>
                          <td className="px-4 py-3 text-foreground-700 hidden md:table-cell">
                            {PAYMENT_LABELS[sale.payment_method] || sale.payment_method}
                          </td>
                          <td className="px-4 py-3 text-foreground-600 hidden md:table-cell">
                            {formatDateTime(sale.created_at)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium text-foreground-950">
                            {formatMoney(Number(sale.total))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}